//#region types
import { FormEnums } from "models/enums";
import { SalaryFormTypes } from "models/formTypes";
import {
  AutoCompleteExtraProps,
  GenericFormSection,
  MonthRangeFormFieldProps,
} from "models/propTypes/genericFormProps";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { WintSuperkollAPIModelWintSalaryReport } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { useCompanyService, useWintSalaryService } from "serviceHooks";
import useSalaryStateHelper from "./useSalaryStateHelper";
import { useDeviationPermissions } from "components/salaryDeviation";
//#endregion
import { DateHelper, FilterHelper, QueryStateHelper } from "utils/helpers";

function useSalaryReportFilterDefinition() {
  const companyService = useCompanyService();
  const { searchPersonsAutoComplete } = useWintSalaryService();
  const { canManageSalary, isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } =
    useDeviationPermissions();
  const { getSalaryStateSelectionItems, getSalaryPaymentStateSelectionItems } = useSalaryStateHelper();

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportFilter
  ): Promise<SalaryFormTypes.WintSalaryReportFilter> => {
    const reportFilter: SalaryFormTypes.WintSalaryReportFilter = {
      yearMonthRange: {},
    };

    if (filter.personIds?.length) {
      const persons = await companyService.getEmployees({ personIds: filter.personIds });
      reportFilter.persons = persons.map((person) => ({ id: person.id, value: person.name ?? "" }));
    }

    if (filter.salaryStates?.length) {
      reportFilter.salaryStates = getSalaryStateSelectionItems().filter((state) =>
        filter.salaryStates?.includes(state.id as WintSuperkollAPIModelWintSalaryReport.WintSalaryState)
      );
    }

    if (filter.paymentStates?.length) {
      reportFilter.paymentStates = getSalaryPaymentStateSelectionItems().filter((state) =>
        filter.paymentStates?.includes(state.id as WintSuperkollAPIModelWintSalaryReport.SalaryPaymentState)
      );
    }

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Salary.WintSalaryReportFilter.YearMonthRange,
      filter.startPeriod ? DateHelper.getDateFromYearMonth(filter.startPeriod) : undefined,
      filter.endPeriod ? DateHelper.getDateFromYearMonth(filter.endPeriod) : undefined
    );

    return reportFilter;
  };

  const filterFormFileds: GenericFormSection<SalaryFormTypes.WintSalaryReportFilter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        yearMonthRange: {
          label: TranslationKeys.Common.TimePeriod,
          type: FormEnums.FormFieldType.MONTHRANGE,
          hideInReadOnlyRemovableView: true,
          extraProps: {
            hideClearable: true,
          } as MonthRangeFormFieldProps,
        },
        persons: {
          label: TranslationKeys.Common.Employee,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Salary.WintSalaryReportFilter.Persons,
            items: (searchStr: string) =>
              searchPersonsAutoComplete(
                searchStr,
                false,
                true,
                isBusinessUnitManagerWithSalaryRights && !(isSalaryAdmin || isDeviationAdmin)
              ),
          } as AutoCompleteExtraProps<SalaryFormTypes.WintSalaryReportFilter>,
          hidden: !canManageSalary,
        },
        salaryStates: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: getSalaryStateSelectionItems(),
          },
        },
        paymentStates: {
          label: TranslationKeys.Common.PaymentStatus,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: getSalaryPaymentStateSelectionItems(),
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<SalaryFormTypes.WintSalaryReportFilter> = {
    persons: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.Salary.WintSalaryReportFilter.Persons
    ),
    salaryStates: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Salary.WintSalaryReportFilter.SalaryStates,
      getSalaryStateSelectionItems()
    ),
    paymentStates: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Salary.WintSalaryReportFilter.PaymentStates,
      getSalaryPaymentStateSelectionItems()
    ),
    yearMonthRange: QueryStateHelper.monthRangeQueryEncoder(
      FormFieldConstants.Salary.WintSalaryReportFilter.YearMonthRange
    ),
  };

  const onClickClearAll = (
    values: SalaryFormTypes.WintSalaryReportFilter,
    setValues: (values: SalaryFormTypes.WintSalaryReportFilter) => void
  ) => {
    setValues({
      yearMonthRange: values.yearMonthRange,
    });
  };

  return { filterFormFileds, filterQueryStateHandler, apiToReportFilterFormatter, onClickClearAll };
}

export default useSalaryReportFilterDefinition;
